import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PictureArucoComponent } from '@modules/aruco/components/picture-aruco/picture-aruco.component';
import { ArucoDetectionService } from '@modules/aruco/services/aruco-detection.service';
import { FormGridComponent } from '@modules/forms/components/form-grid/form-grid.component';
import { provideFormGrid } from '@modules/forms/components/form-grid/form-grid.providers';
import { FormGridGroupMode } from '@modules/forms/components/form-grid/models/form-grid-group-mode';
import { FormGridService } from '@modules/forms/components/form-grid/services/form-grid.service';
import { FileWithModuleId } from '@modules/forms/models/file-with-module-id';
import { SadmGridHeaderCellComponent } from '@modules/sadm/components/sadm-grid/sadm-grid-header-cell/sadm-grid-header-cell.component';
import { SadmGridInputs } from '@modules/sadm/models/sadm-grid-inputs';
import { SequenceMediaComponent } from '@modules/sequence/sequence-media/sequence-media.component';
import { PatientSequencePickerService } from '@modules/sequence/services/patient-sequence-picker.service';
import { SequencePaginationService } from '@modules/sequence/services/sequence-pagination.service';
import { ImageInputComponent } from '@modules/shared/image-input/image-input.component';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { MediaListItemContext } from '@modules/shared/media-picker/models/media-list-item-context';
import { SavingNotificationComponent } from '@modules/shared/saving-notification/saving-notification.component';
import { TopBarConfig } from '@modules/shared/top-bar/top-bar-config';
import { TopBarComponent } from '@modules/shared/top-bar/top-bar.component';
import { Media } from '@pixacare/domain';
import { DialogService } from '@services/dialog.service';
import { ErrorIconComponent } from '@shared/components/error-icon/error-icon.component';
import { TuiButton, TuiDialogContext, TuiNotificationService, TuiScrollbar } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { concat, defer, EMPTY, timeout, timer } from 'rxjs';
import { catchError, filter, finalize, ignoreElements, take, tap } from 'rxjs/operators';
import { toSadmEntityChangeset } from './adapters/sadm-entity-changeset.adapter';
import { createDraftGridEntity, toSadmGridEntity } from './adapters/sadm-grid-entity.adapter';
import { SadmEntityChangeset } from './models/sadm-entity-changeset';
import { SadmEntityChangesetPartialAction } from './models/sadm-entity-changeset-partial';
import { changesetActionToGroupType, sadmEntityChangesetFormTypes } from './models/sadm-entity-changeset-partial.utils';
import { SadmGridEntity } from './models/sadm-grid-entity';
import {
  SAVE_ERROR_KEY,
  SAVE_INCOMPLETE_FORMS_KEY,
  SAVE_PROGRESS_KEYS,
  SAVE_SUCCESS_CLOSE_DELAY_MS,
  SAVE_SUCCESS_KEY,
  SAVE_TIMEOUT_MS,
} from './sadm-grid-save.config';
import { provideSadmGrid } from './sadm-grid.providers';
import { SadmFormGroupsBuilderService } from './services/sadm-form-groups-builder.service';
import { SadmGridDataService } from './services/sadm-grid-data.service';
import { SadmGridGalleryService } from './services/sadm-grid-gallery.service';
import { SadmGridSaveService } from './services/sadm-grid-save.service';
import { SadmGridStore } from './services/sadm-grid-store';
import { SadmGridStoreBridgeService } from './services/sadm-grid-store-bridge.service';
import { SadmGridTopBarBuilderService } from './services/sadm-grid-top-bar-builder.service';

@Component({
  selector: 'pxc-sadm-grid',
  templateUrl: './sadm-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex h-full flex-col absolute inset-0 overflow-hidden',
  },
  providers: [
    provideSadmGrid(),
    ArucoDetectionService,
    SequencePaginationService,
    PatientSequencePickerService,
    DatePipe,
    provideFormGrid(),
  ],
  imports: [
    TranslocoDirective,
    LoaderComponent,
    TopBarComponent,
    SadmGridHeaderCellComponent,
    FormGridComponent,
    ImageInputComponent,
    SavingNotificationComponent,
    FormsModule,
    TuiScrollbar,
    TuiButton,
    ErrorIconComponent,
    PictureArucoComponent,
    SequenceMediaComponent,
  ],
})
export class SadmGridComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly context = inject<TuiDialogContext<void, SadmGridInputs>>(POLYMORPHEUS_CONTEXT);
  private readonly dataService = inject(SadmGridDataService);
  private readonly saveService = inject(SadmGridSaveService);
  private readonly galleryService = inject(SadmGridGalleryService);
  private readonly dialogService = inject(DialogService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sadmGridStore = inject(SadmGridStore);
  private readonly storeBridge = inject(SadmGridStoreBridgeService);
  private readonly formGroupsBuilder = inject(SadmFormGroupsBuilderService);
  private readonly topBarBuilder = inject(SadmGridTopBarBuilderService);
  private readonly formGridService = inject(FormGridService);
  private readonly patientSequencePickerService = inject(PatientSequencePickerService);

  readonly sadmClient = this.context.data.sadmClient;
  readonly patient = this.context.data.patient;
  readonly clientCode: string = ((): string => {
    const code = this.patient.clientCode;
    if (!code) {
      throw new Error('SadmGridComponent: patient.clientCode is required');
    }
    return code;
  })();

  private readonly isSaving = signal(false);
  private readonly isMediaValidationTriggered = signal(false);
  private readonly savingNotification = viewChild(SavingNotificationComponent);
  private readonly headerTemplate = viewChild<TemplateRef<{ $implicit: SadmGridEntity }>>('headerTemplate');
  private readonly analysisProjection =
    viewChild<TemplateRef<{ $implicit: SadmGridEntity; mode: FormGridGroupMode }>>('analysisProjection');
  private readonly arucoItemTemplate = viewChild.required<TemplateRef<MediaListItemContext>>('arucoItem');

  readonly formGroups = computed(() => {
    const analysisProjection = this.analysisProjection();
    if (!analysisProjection) {
      return null;
    }
    return this.formGroupsBuilder.build({
      analysisProjection,
      moduleInputs: (entity) => this.sadmGridStore.moduleInputs(entity),
      onAnalysisModuleTriggered: (entity, event) => this.sadmGridStore.onModuleApplied(entity.entityKey, event),
      onAnalysisModuleReset: (entity, event) => this.sadmGridStore.onModuleReset(entity.entityKey, event),
    });
  });

  readonly gridConfig = computed(() => {
    const headerTemplate = this.headerTemplate();
    const formGroups = this.formGroups();
    if (!this.sadmGridStore.ready() || !headerTemplate || !formGroups) {
      return null;
    }
    return {
      context: this.sadmClient,
      entities: this.sadmGridStore.entities(),
      formGroups,
      headerCell: headerTemplate,
    };
  });

  constructor() {
    this.dataService
      .loadEntities(this.sadmClient.id, this.clientCode, this.patient)
      .pipe(takeUntilDestroyed())
      .subscribe((entities) => {
        this.storeBridge.load(entities, this.clientCode);
        this.sadmGridStore.init(entities.map(toSadmGridEntity));
      });

    // Auto-add a first draft column when the grid opens with no entities,
    // landing the user directly on a fresh draft instead of an empty grid.
    toObservable(this.shouldAutoAddDraft)
      .pipe(filter(Boolean), take(1), takeUntilDestroyed())
      .subscribe(() => this.addDraft());
  }

  private readonly shouldAutoAddDraft = computed(() => {
    const config = this.gridConfig();
    return !!config && this.formGridService.isInitialized() && config.entities.length === 0;
  });

  woundMedia(entity: SadmGridEntity): { medias: Signal<Media[]>; files: Signal<FileWithModuleId[]> } {
    return this.sadmGridStore.get(entity.entityKey)!;
  }

  onFilesChange(entityKey: string, files: File[]): void {
    this.sadmGridStore.setFiles(entityKey, files);
  }

  openPicker(entity: SadmGridEntity): void {
    const current = this.sadmGridStore.get(entity.entityKey);
    if (!current) {
      return;
    }
    this.patientSequencePickerService
      .open({
        patient: this.patient,
        preselectedMediaIds: current.medias().map((media) => media.id),
        preselectedFiles: current.files(),
        itemTemplate: this.arucoItemTemplate(),
        label: this.translocoService.translate('Lésion'),
        subtitle: this.translocoService.translate('Choisir les photos à associer à cette lésion'),
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((selection) => {
        if (!selection) {
          return;
        }
        this.sadmGridStore.setMedias(entity.entityKey, selection.medias);
        this.sadmGridStore.setFiles(entity.entityKey, selection.files);
      });
  }

  onMediaDelete(entityKey: string, mediaId: number): void {
    this.sadmGridStore.removeMedia(entityKey, mediaId);
  }

  addDraft(): void {
    const draft = createDraftGridEntity();

    this.sadmGridStore.addDraft(draft);
    // Open the draft directly in add mode so every configured form type is ready for input.
    // Only the ones actually edited are saved (changeset is dirty-gated) and validated.
    this.formGridService.addEntity(draft, 'add');
  }

  openGallery(entity: SadmGridEntity, mediaIndex: number): void {
    this.galleryService.openGallery(this.clientCode, entity.entityKey, mediaIndex);
  }

  isMediaRequiredError(entityKey: string): boolean {
    // Photo required only when the analysis is actually saved (an untouched add-mode analysis must not force it).
    const isAnalysisSaved = this.changesets().some((changeset) => changeset.key === entityKey && !!changeset.analysis);
    return this.isMediaValidationTriggered() && isAnalysisSaved && !this.sadmGridStore.hasMedia(entityKey);
  }

  readonly changesets = computed<SadmEntityChangeset[]>(() => {
    if (!this.sadmGridStore.ready()) {
      return [];
    }
    return this.sadmGridStore
      .snapshots()
      .map((snapshot) =>
        toSadmEntityChangeset(snapshot, {
          creation: this.formGridService.instance(snapshot.entity.entityKey, 'creation'),
          protocol: this.formGridService.instance(snapshot.entity.entityKey, 'protocol'),
          analysis: this.formGridService.instance(snapshot.entity.entityKey, 'analysis'),
        }),
      )
      .filter((changeset): changeset is SadmEntityChangeset => changeset !== null);
  });

  readonly isDirty = computed<boolean>(() => this.changesets().length > 0);
  private readonly isSaveDisabled = computed<boolean>(() => !this.isDirty() || this.isSaving());

  save(): void {
    if (this.isSaveDisabled()) {
      return;
    }
    const changesets = this.changesets();

    // Validate every form the changeset will write. A new draft's changeset type always carries a
    // creation answer (saving a draft creates the entity that protocol/analysis depend on) — so an
    // empty required creation form blocks the save here too.
    const validationTargets = changesets.flatMap((changeset) =>
      sadmEntityChangesetFormTypes(changeset).map((formType) => ({ entityKey: changeset.key, formType })),
    );
    const areFormsValid = this.formGridService.reportValidity(validationTargets);
    const isMediaValid = this.reportMediaValidity();
    if (!areFormsValid || !isMediaValid) {
      this.alertService
        .open(this.translocoService.translate(SAVE_INCOMPLETE_FORMS_KEY), { appearance: 'negative' })
        .subscribe();
      return;
    }

    const notification = this.savingNotification();
    const errorMessage = this.translocoService.translate(SAVE_ERROR_KEY);
    this.isSaving.set(true);
    notification?.start(SAVE_PROGRESS_KEYS.map((key) => this.translocoService.translate(key)));
    this.markChangesetsOngoing(changesets);

    let hasErrors = false; // ≥1 partial returned success:false (stream still completes normally)
    let isTimedOut = false; // timeout fired — stream died before all partials were received

    concat(
      // Phase 1: stream partial save results from the server
      this.saveService.save({ sadmClient: this.sadmClient, patient: this.patient, changesets }).pipe(
        timeout(SAVE_TIMEOUT_MS),
        tap((partial) => {
          if (!partial.success) {
            hasErrors = true;
            this.formGridService.markSaveResult(
              partial.key,
              changesetActionToGroupType(partial.action),
              false,
              errorMessage,
            );
          } else {
            this.finalizeSavedFormGroup(partial.key, partial.action);
          }
          this.sadmGridStore.applySavePartial(partial);
          this.storeBridge.save(partial, this.clientCode);
        }),
        catchError(() => {
          isTimedOut = true;
          notification?.error(errorMessage);
          return EMPTY;
        }),
      ),
      // Phase 2: show final notification once all partials are processed
      defer(() => {
        if (isTimedOut) {
          return EMPTY;
        }
        if (hasErrors) {
          notification?.error(errorMessage);
          return EMPTY;
        }
        notification?.success(this.translocoService.translate(SAVE_SUCCESS_KEY));
        return timer(SAVE_SUCCESS_CLOSE_DELAY_MS);
      }),
    )
      .pipe(
        ignoreElements(),
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () => {
          if (!isTimedOut && !hasErrors) {
            this.context.completeWith();
          }
        },
      });
  }

  private reportMediaValidity(): boolean {
    this.isMediaValidationTriggered.set(true);
    return this.sadmGridStore.entities().every((entity) => !this.isMediaRequiredError(entity.entityKey));
  }

  private finalizeSavedFormGroup(entityKey: string, action: SadmEntityChangesetPartialAction): void {
    if (action === 'medias-attach' || action === 'media-remove') {
      return;
    }
    this.formGridService.markSaveResult(entityKey, changesetActionToGroupType(action), true);
  }

  private markChangesetsOngoing(changesets: readonly SadmEntityChangeset[]): void {
    for (const changeset of changesets) {
      for (const formType of sadmEntityChangesetFormTypes(changeset)) {
        this.formGridService.markSaving(changeset.key, formType);
      }
    }
  }

  private requestClose(): void {
    if (!this.isDirty()) {
      this.context.completeWith();
      return;
    }
    this.dialogService.exitConfirmDialog.subscribe((confirmed) => {
      if (confirmed) {
        this.context.completeWith();
      }
    });
  }

  readonly topBarConfig: TopBarConfig = this.topBarBuilder.build({
    saveDisabled$: toObservable(this.isSaveDisabled),
    onClose: () => this.requestClose(),
    onSave: () => this.save(),
  });
}
